import React, { FC, useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { FlowShell } from "@/common/components/FlowShell/flow-shell";
import { getString } from "@/common/uistringlabels/ui-string-utils";
import { useFlowNavigation } from "@/common/flow/use-flow-navigation";
import { Paths } from "@/common/paths/paths";
import { IT_SHOP_URL } from "@/config";
import {
  PassedDeviceContext,
  useReplacedDevice,
} from "@/pages/Replacement/hooks/use-replaced-device.hook";
import {
  GENERATE_SUPPORT_TICKET_URL,
  HARDWARE_ELIGIBILITY_POLICY_URL,
} from "@/pages/Replacement/replacement.constants";
import { ReplacementNavState } from "@/pages/Replacement/replacement.types";
import { replacementSteps } from "@/pages/Replacement/replacement-steps";
import { useReplacementDraftStore } from "@/pages/Replacement/use-replacement-draft-store";
import { ChooseDeviceStep } from "@/pages/Replacement/steps/ChooseDevice/choose-device-step";
import { ChooseDeliveryStep } from "@/pages/Replacement/steps/ChooseDelivery/choose-delivery-step";
import { ReviewStep } from "@/pages/Replacement/steps/Review/review-step";
import { usePlaceReplacementOrder } from "@/pages/Replacement/steps/Review/hooks/use-place-replacement-order.hook";
import { ConfirmationView } from "@/pages/Replacement/confirmation/confirmation-view";
import type { SubmitFailure } from "@/pages/Replacement/steps/Review/review.types";
import type { ReplacementOrderResult } from "@/services/replacement-order/replacement-order.types";
import { isRetryableErrorCode } from "@/services/replacement-order/replacement-order.constants";

interface ReplacementStepContentProps {
  stepId: string;
  assetId: string;
  passedContext?: PassedDeviceContext;
  category: string;
  hasGateError: boolean;
  submitFailure?: SubmitFailure;
  onEditDevice: () => void;
  onEditDelivery: () => void;
}

const ReplacementStepContent: FC<ReplacementStepContentProps> = ({
  stepId,
  assetId,
  passedContext,
  category,
  hasGateError,
  submitFailure,
  onEditDevice,
  onEditDelivery,
}) => {
  switch (stepId) {
    case "choose-device":
      return (
        <ChooseDeviceStep
          assetId={assetId}
          passedContext={passedContext}
          policyUrl={HARDWARE_ELIGIBILITY_POLICY_URL}
          supportTicketHref={GENERATE_SUPPORT_TICKET_URL}
        />
      );
    case "choose-delivery":
      return (
        <ChooseDeliveryStep
          assetId={assetId}
          passedContext={passedContext}
          supportTicketHref={GENERATE_SUPPORT_TICKET_URL}
          hasGateError={hasGateError}
        />
      );
    case "review":
      return (
        <ReviewStep
          assetId={assetId}
          category={category}
          onEditDevice={onEditDevice}
          onEditDelivery={onEditDelivery}
          submitFailure={submitFailure}
        />
      );
    default:
      return null;
  }
};

const ReplacementFlowPage: FC = () => {
  const { assetTag } = useParams<{ assetTag: string }>();
  const { state } = useLocation<ReplacementNavState | undefined>();

  const passedContext = state
    ? { category: state.category, modelName: state.modelName }
    : undefined;
  const identifier = state?.assetId ?? assetTag ?? "";

  const { device } = useReplacedDevice(identifier, passedContext);
  const modelName = device?.modelName ?? "device";
  const category = device?.category ?? "";

  const draft = useReplacementDraftStore();
  const [currentStep, setCurrentStep] = useState(0);
  const [orderResult, setOrderResult] = useState<ReplacementOrderResult>();
  const navigation = useFlowNavigation({
    steps: replacementSteps,
    draft,
    currentStep,
    setCurrentStep,
  });
  const { placeOrder, isPending, failure, reset } = usePlaceReplacementOrder({
    assetId: identifier,
    category,
    onOrderPlaced: setOrderResult,
  });

  const isReview = navigation.currentStepId === "review";

  useEffect(() => {
    if (!isReview) reset();
  }, [isReview, reset]);

  if (orderResult) {
    return <ConfirmationView assetId={identifier} category={category} orderResult={orderResult} />;
  }

  return (
    <FlowShell
      crumbs={[
        { title: getString("replacement.breadcrumb.itShop"), url: IT_SHOP_URL },
        { title: getString("replacement.breadcrumb.myHardware"), url: Paths.myHardware.url },
        { title: getString("replacement.breadcrumb.current", { model: modelName }) },
      ]}
      heading={getString("replacement.heading", { model: modelName })}
      headingVisibility="sr-only"
      stepLabels={navigation.stepLabels}
      progressLabel={getString("replacement.progressLabel")}
      currentStep={navigation.currentStep}
      onStepSelected={navigation.goToStep}
      onBack={navigation.canGoBack ? navigation.goBack : undefined}
      onContinue={isReview ? placeOrder : navigation.goNext}
      continueLabel={getString(
        isReview ? "replacement.placeOrderLabel" : "replacement.continueLabel"
      )}
      continueBusy={isReview ? isPending : undefined}
      continueDisabled={isReview && failure ? !isRetryableErrorCode(failure.code) : undefined}
      backLabel={getString("replacement.backLabel")}
      errorMessage={navigation.errorMessage}
    >
      <ReplacementStepContent
        stepId={navigation.currentStepId}
        assetId={identifier}
        passedContext={passedContext}
        category={category}
        hasGateError={Boolean(navigation.errorMessage)}
        submitFailure={failure}
        onEditDevice={() => navigation.goToStep(0)}
        onEditDelivery={() => navigation.goToStep(1)}
      />
    </FlowShell>
  );
};

export default ReplacementFlowPage;
